import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { sortClubLifeFeedPosts } from "../../../utils/clubLifeFeedSort";
import clinicAPI from "../../../services/clinicApi";
import { normalizeRole } from "../../../utils/roles";
import ClubLifePostCard from "./ClubLifePostCard";
import ClubLifeRepostModal from "../common/ClubLifeRepostModal";
import ClubLifeComposer from "../PatientPortal/ClubLifeComposer";
import ClientPostManageModals from "../PatientPortal/ClientPostManageModals";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useClientPostManage } from "../../../hooks/useClientPostManage";
import { useToast } from "../../ui";
import cs from "../PatientPortal/clientApp.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import md from "./manageDesktop.module.css";

export default function ClubLifeHomeSection({
  title = "Клуб",
  feedLink,
  feedLabel = "Лента",
  limit = null,
  mode = "admin",
  appMode = false,
  showComposer = true,
  desktop = false,
}) {
  const toast = useToast();
  const isAdmin = mode === "admin";
  const { profile, role } = useSelector((state) => state.auth);
  const userRole = normalizeRole(role);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repostTarget, setRepostTarget] = useState(null);
  const [repostBusy, setRepostBusy] = useState(false);

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(setPosts, {
    commentsApi: isAdmin ? "admin" : "client",
  });
  const postManage = useClientPostManage(setPosts, {
    apiMode: isAdmin ? "staff" : "client",
  });

  const canManagePost = useMemo(
    () => (post) => {
      if (!isAdmin) {
        return (
          Boolean(profile?.id) &&
          String(post.author?.id) === String(profile.id)
        );
      }
      if (post.author?.id && profile?.id && post.author.id === profile.id) {
        return true;
      }
      return userRole === "owner" || userRole === "admin";
    },
    [isAdmin, profile?.id, userRole],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = limit != null ? { limit: Math.max(limit, 3) } : {};
      const data = isAdmin
        ? await clinicAPI.getClubLifePosts(params)
        : await clinicAPI.getClubLifeFeed(params);
      const rows = sortClubLifeFeedPosts(data.posts || []);
      setPosts(limit != null ? rows.slice(0, limit) : rows);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRepostRequest = isAdmin
    ? (post) => setRepostTarget(post)
    : undefined;

  const confirmRepost = async (comment) => {
    if (!repostTarget || repostBusy || !isAdmin) return;
    setRepostBusy(true);
    try {
      await clinicAPI.repostClubLifePost({
        post_id: repostTarget.id,
        comment,
      });
      toast.success("Репост опубликован");
      setRepostTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось сделать репост");
    } finally {
      setRepostBusy(false);
    }
  };

  if (!showComposer && !feedLink && !loading && posts.length === 0) {
    return null;
  }

  const cardMode = isAdmin ? "admin" : "client";
  const sectionClass = [
    desktop ? md.desktopClubLife : cs.homeSection,
    desktop ? null : cs.homeCard,
    appMode ? aa.clubLifeHomeBlock : "",
  ]
    .filter(Boolean)
    .join(" ");

  const repostModal = isAdmin ? (
    <ClubLifeRepostModal
      post={repostTarget}
      open={Boolean(repostTarget)}
      busy={repostBusy}
      onClose={() => !repostBusy && setRepostTarget(null)}
      onConfirm={confirmRepost}
    />
  ) : null;

  if (desktop) {
    return (
      <>
        {repostModal}
        <ClientPostManageModals {...postManage} />
        <section className={sectionClass} aria-label={title}>
          <div className={md.desktopClubLifeHead}>
            <h2 className={md.desktopClubLifeTitle}>{title}</h2>
            {feedLink ? (
              <Link to={feedLink} className={md.desktopClubLifeLink}>
                {feedLabel}
              </Link>
            ) : null}
          </div>

          {showComposer ? (
            <div className={md.desktopClubLifeComposer}>
              <ClubLifeComposer
                compact
                embedded
                variant={isAdmin ? "admin" : "client"}
                onPublished={load}
              />
            </div>
          ) : null}

          {!loading && posts.length > 0 ? (
            <div className={md.desktopClubLifeFeed}>
              {posts.map((post) => (
                <ClubLifePostCard
                  key={post.id}
                  post={post}
                  mode={cardMode}
                  currentUserId={profile?.id}
                  onOpenMenu={postManage.openMenu}
                  canManagePost={canManagePost}
                  onLike={toggleLike}
                  onLoadComments={loadComments}
                  commentsOpen={openComments === post.id}
                  comments={commentsByPost[post.id] || []}
                  onComment={addComment}
                  commentBusy={commentBusy}
                  onRepost={handleRepostRequest}
                />
              ))}
            </div>
          ) : null}
        </section>
      </>
    );
  }

  return (
    <>
      {repostModal}
      <ClientPostManageModals {...postManage} />
      <section className={sectionClass} aria-label={title}>
        <div className={cs.homeCardHead}>
          <h2 className={cs.homeSectionTitle}>{title}</h2>
          {feedLink ? (
            <Link to={feedLink} className={cs.homeSectionLink}>
              {feedLabel}
            </Link>
          ) : null}
        </div>

        {showComposer ? (
          <div className={cs.homeComposerWrap}>
            <ClubLifeComposer
              compact
              embedded
              variant={isAdmin ? "admin" : "client"}
              onPublished={load}
            />
          </div>
        ) : null}

        {!loading && posts.length > 0 ? (
          <div className={cs.homeFeed}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode={cardMode}
                currentUserId={profile?.id}
                onOpenMenu={postManage.openMenu}
                canManagePost={canManagePost}
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
                onRepost={handleRepostRequest}
              />
            ))}
          </div>
        ) : null}
      </section>
    </>
  );
}
